/**
 * Horloge locale — le cadran de l'outil « quelle heure est-il à… ? ».
 *
 * Le serveur a déjà répondu : l'heure, la date et le fuseau sont ÉCRITS dans la page, et la page
 * est juste sans ce fichier. Ce composant ajoute deux choses, et rien d'autre — un cadran, et le
 * fait qu'il avance.
 *
 * TROIS DÉCISIONS VALENT D'ÊTRE DITES.
 *
 * 1. L'heure vient du SERVEUR, pas de la machine du visiteur. `data-epoch` porte l'instant du
 *    rendu ; on en tire une fois pour toutes l'écart avec `Date.now()` et on l'applique à chaque
 *    battement. Sans ça, une machine mal réglée afficherait très sérieusement une fausse heure de
 *    Tokyo — et l'outil n'aurait servi à rien du tout.
 *
 * 2. Le découpage en heures/minutes/secondes passe par `Intl.DateTimeFormat` avec `timeZone`, pas
 *    par une addition de décalage. Le décalage change deux fois par an, et il peut changer PENDANT
 *    qu'on regarde la page : à 3 h du matin le dernier dimanche d'octobre, une horloge qui ajoute
 *    un nombre de minutes figé se met à mentir d'une heure. `Intl` interroge la base tz du
 *    navigateur, qui sait. `data-offset-minutes` reste le filet, pour le cas où le fuseau serait
 *    inconnu de ce navigateur-là.
 *
 * 3. Le cadran arrive `hidden` et c'est nous qui le découvrons. Un canevas est hors d'atteinte du
 *    CSS : laissé visible, il ferait un carré vide sur tout navigateur sans JavaScript, à la place
 *    de la réponse. Symétriquement, la note « heure figée » que le serveur a écrite s'efface ici :
 *    elle n'est plus vraie dès que la trotteuse tourne.
 */

/** Le pas d'un cadran : une seconde. Plus fin ne se voit pas, et réveille l'écran pour rien. */
const TICK_MS = 1000;

/**
 * Peintes ici, comme pour la lune et la roue du ciel : aucune feuille de style n'atteint un canevas,
 * donc chaque thème garde sa copie de la palette (cf. sky-chart/core/constants.js).
 */
const PALETTE = {
	face: '#fffaf2',
	rim: 'rgba(199, 180, 240, 0.55)',
	tickHour: 'rgba(90, 74, 120, 0.75)',
	tickMinute: 'rgba(90, 74, 120, 0.28)',
	hourHand: '#5a4a78',
	minuteHand: '#5a4a78',
	secondHand: '#e8748f',
	pin: '#5a4a78',
};

/**
 * Les composantes de l'heure qu'il est dans `zone` à l'instant `date`.
 *
 * Rend `null` quand le navigateur ne connaît pas le fuseau — l'appelant retombe alors sur le
 * décalage que le serveur a posé. On préfère un `null` franc à un silence : `Intl` accepte
 * volontiers un fuseau inconnu sur certains moteurs et répond en UTC, ce qui serait une heure
 * fausse rendue avec assurance.
 */
function partsInZone(date, zone) {
	try {
		const parts = new Intl.DateTimeFormat('en-GB', {
			timeZone: zone,
			hour12: false,
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		}).formatToParts(date);
		const get = (type) => Number((parts.find((part) => part.type === type) || {}).value);
		const hour = get('hour');
		const minute = get('minute');
		const second = get('second');
		if ([hour, minute, second].some(Number.isNaN)) return null;
		// `en-GB` en 24 h rend minuit « 24 » sur quelques moteurs. Un cadran qui affiche 24:07 est
		// faux deux fois : l'heure n'existe pas, et l'aiguille part un tour trop loin.
		return { hour: hour % 24, minute, second };
	} catch (unknownZone) {
		return null;
	}
}

/** Le repli : le décalage figé que le serveur a mesuré au rendu. Juste, sauf en travers d'un changement d'heure. */
function partsByOffset(date, offsetMinutes) {
	const shifted = new Date(date.getTime() + offsetMinutes * 60000);
	return {
		hour: shifted.getUTCHours(),
		minute: shifted.getUTCMinutes(),
		second: shifted.getUTCSeconds(),
	};
}

function two(value) {
	return String(value).padStart(2, '0');
}

class Dial {

	constructor(canvas) {
		this.canvas = canvas;
		this.ctx = canvas.getContext('2d');

		// La taille INTRINSÈQUE du canevas est sa taille en pixels CSS ; la mémoire de dessin suit la
		// densité de l'écran. Sans ce doublement, les aiguilles sortent floues sur tout écran dense —
		// même piège, même remède que le glyphe de lune.
		const css = { w: canvas.width || 300, h: canvas.height || 300 };
		const dpr = Math.min(window.devicePixelRatio || 1, 3);
		canvas.width = Math.round(css.w * dpr);
		canvas.height = Math.round(css.h * dpr);
		// La HAUTEUR reste `auto`, et ce n'est pas un détail : le reset donne `max-width: 100%` à tout
		// canevas, si bien qu'une largeur écrite en dur ici se fait rogner sous ~430px pendant que la
		// hauteur, elle, tient bon — le cadran sortait alors en OVALE (mesuré sur fastast : 258×300 à
		// 390px, 188×300 à 320px). Laissée `auto`, la hauteur suit le rapport intrinsèque de la mémoire
		// de dessin, qui est carrée : le cadran rétrécit rond.
		canvas.style.width = `${css.w}px`;
		canvas.style.height = 'auto';
		this.ctx.scale(dpr, dpr);

		this.cx = css.w / 2;
		this.cy = css.h / 2;
		this.radius = Math.min(this.cx, this.cy) - 4;
	}

	render({ hour, minute, second }) {
		const ctx = this.ctx;
		ctx.clearRect(0, 0, this.cx * 2, this.cy * 2);

		ctx.beginPath();
		ctx.arc(this.cx, this.cy, this.radius, 0, Math.PI * 2);
		ctx.fillStyle = PALETTE.face;
		ctx.fill();
		ctx.lineWidth = 2;
		ctx.strokeStyle = PALETTE.rim;
		ctx.stroke();

		// Soixante graduations, dont douze longues : c'est ce qui fait lire un cadran d'un coup d'œil
		// plutôt que de le déchiffrer. Pas de chiffres — ils demandent une police, et une police
		// demande une taille, et une taille demande de choisir entre lisible et encombré.
		for (let index = 0; index < 60; index += 1) {
			const isHour = index % 5 === 0;
			const angle = (index / 60) * Math.PI * 2;
			const outer = this.radius - 6;
			const inner = outer - (isHour ? 10 : 5);
			ctx.beginPath();
			ctx.moveTo(this.cx + Math.sin(angle) * inner, this.cy - Math.cos(angle) * inner);
			ctx.lineTo(this.cx + Math.sin(angle) * outer, this.cy - Math.cos(angle) * outer);
			ctx.lineWidth = isHour ? 2.5 : 1;
			ctx.strokeStyle = isHour ? PALETTE.tickHour : PALETTE.tickMinute;
			ctx.stroke();
		}

		// L'aiguille des heures avance AVEC les minutes, et celle des minutes avec les secondes : une
		// aiguille qui saute de graduation en graduation se lit comme une horloge arrêtée qui repart.
		const seconds = second;
		const minutes = minute + seconds / 60;
		const hours = (hour % 12) + minutes / 60;

		this.hand((hours / 12) * Math.PI * 2, this.radius * 0.52, 5, PALETTE.hourHand);
		this.hand((minutes / 60) * Math.PI * 2, this.radius * 0.76, 3.5, PALETTE.minuteHand);
		this.hand((seconds / 60) * Math.PI * 2, this.radius * 0.82, 1.5, PALETTE.secondHand);

		ctx.beginPath();
		ctx.arc(this.cx, this.cy, 4, 0, Math.PI * 2);
		ctx.fillStyle = PALETTE.pin;
		ctx.fill();
	}

	hand(angle, length, width, color) {
		const ctx = this.ctx;
		// Un léger contre-poids derrière l'axe : c'est ce qui distingue une aiguille d'un rayon.
		const tail = length * 0.12;
		ctx.beginPath();
		ctx.moveTo(this.cx - Math.sin(angle) * tail, this.cy + Math.cos(angle) * tail);
		ctx.lineTo(this.cx + Math.sin(angle) * length, this.cy - Math.cos(angle) * length);
		ctx.lineWidth = width;
		ctx.lineCap = 'round';
		ctx.strokeStyle = color;
		ctx.stroke();
	}

}

function attach(root) {
	const canvas = root.querySelector('[data-local-clock-dial]');
	const timeText = root.querySelector('[data-local-clock-time]');
	const note = root.querySelector('[data-local-clock-note]');
	if (!canvas) return;

	const zone = root.dataset.timezone || 'UTC';
	const offsetMinutes = Number(root.dataset.offsetMinutes || 0);
	const epoch = Number(root.dataset.epoch || 0);
	// L'écart entre l'horloge du serveur et celle d'ici, mesuré UNE fois. Le mesurer à chaque
	// battement ferait dériver l'affichage au rythme des arrondis.
	const skew = epoch > 0 ? epoch * 1000 - Date.now() : 0;

	const dial = new Dial(canvas);
	canvas.hidden = false;
	if (note) note.hidden = true;

	const tick = () => {
		const now = new Date(Date.now() + skew);
		const parts = partsInZone(now, zone) || partsByOffset(now, offsetMinutes);
		dial.render(parts);
		if (timeText) timeText.textContent = `${two(parts.hour)}:${two(parts.minute)}`;
	};

	tick();
	const timer = window.setInterval(tick, TICK_MS);

	// Un onglet en arrière-plan voit son minuteur bridé : au retour, l'heure affichée peut avoir
	// plusieurs secondes de retard. On rattrape à la volée plutôt que d'attendre le battement suivant.
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) tick();
	});

	return timer;
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-local-clock]').forEach(attach);
});
